import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const DaycarePackagesModal = ({ isOpen, onClose, onSuccess, packageData, apiCall, showError }) => {
  const [formData, setFormData] = useState({
    packageName: '',
    description: '',
    minAgeMonths: '',
    maxAgeMonths: '',
    ageGroupName: '',
    timeSlot: '',
    startTime: '',
    endTime: '',
    daysPerWeek: 5,
    availableDays: 'Monday-Friday',
    monthlyRate: '',
    includesBreakfast: false,
    includesLunch: false,
    includesSnacks: false,
    mealDescription: '',
    maxCapacity: '',
    teacherToChildRatio: '1:4',
    academicYear: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (packageData) {
      setFormData({
        packageName: packageData.PackageName || '',
        description: packageData.Description || '',
        minAgeMonths: packageData.MinAgeMonths || '',
        maxAgeMonths: packageData.MaxAgeMonths || '',
        ageGroupName: packageData.AgeGroupName || '',
        timeSlot: packageData.TimeSlot || '',
        startTime: packageData.StartTime || '',
        endTime: packageData.EndTime || '',
        daysPerWeek: packageData.DaysPerWeek || 5,
        availableDays: packageData.AvailableDays || 'Monday-Friday',
        monthlyRate: packageData.MonthlyRate || '',
        includesBreakfast: packageData.IncludesBreakfast || false,
        includesLunch: packageData.IncludesLunch || false,
        includesSnacks: packageData.IncludesSnacks || false,
        mealDescription: packageData.MealDescription || '',
        maxCapacity: packageData.MaxCapacity || '',
        teacherToChildRatio: packageData.TeacherToChildRatio || '1:4',
        academicYear: packageData.AcademicYear || ''
      });
    }
  }, [packageData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.packageName || !formData.monthlyRate || !formData.academicYear) {
      showError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (packageData) {
        // Update existing package
        await apiCall(`/daycare/packages/${packageData.PackageID}`, 'PUT', formData);
      } else {
        // Create new package
        await apiCall('/daycare/packages', { method: 'POST', body: JSON.stringify(formData) });
      }
      onSuccess();
    } catch (error) {
      showError('Error saving package: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                {packageData ? 'Edit Package' : 'Create New Package'}
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Package Name */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Package Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.packageName}
                    onChange={(e) => setFormData({ ...formData, packageName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Age Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Age (months)
                  </label>
                  <input
                    type="number"
                    value={formData.minAgeMonths}
                    onChange={(e) => setFormData({ ...formData, minAgeMonths: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Age (months)
                  </label>
                  <input
                    type="number"
                    value={formData.maxAgeMonths}
                    onChange={(e) => setFormData({ ...formData, maxAgeMonths: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Age Group Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Age Group Name
                  </label>
                  <input
                    type="text"
                    value={formData.ageGroupName}
                    onChange={(e) => setFormData({ ...formData, ageGroupName: e.target.value })}
                    placeholder="e.g., 3-8 أشهر"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Time Slot */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time Slot Name
                  </label>
                  <input
                    type="text"
                    value={formData.timeSlot}
                    onChange={(e) => setFormData({ ...formData, timeSlot: e.target.value })}
                    placeholder="e.g., صباحاً حتى 12:30 ب.ظ"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Start Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* End Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Days Per Week */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Days Per Week
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="7"
                    value={formData.daysPerWeek}
                    onChange={(e) => setFormData({ ...formData, daysPerWeek: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Available Days */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Available Days
                  </label>
                  <input
                    type="text"
                    value={formData.availableDays}
                    onChange={(e) => setFormData({ ...formData, availableDays: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Monthly Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monthly Rate ($) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monthlyRate}
                    onChange={(e) => setFormData({ ...formData, monthlyRate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Academic Year */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Academic Year <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.academicYear}
                    onChange={(e) => setFormData({ ...formData, academicYear: e.target.value })}
                    placeholder="e.g., 2025-2026"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Meals */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Included Meals
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.includesBreakfast}
                        onChange={(e) => setFormData({ ...formData, includesBreakfast: e.target.checked })}
                        className="mr-2"
                      />
                      Breakfast
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.includesLunch}
                        onChange={(e) => setFormData({ ...formData, includesLunch: e.target.checked })}
                        className="mr-2"
                      />
                      Lunch
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.includesSnacks}
                        onChange={(e) => setFormData({ ...formData, includesSnacks: e.target.checked })}
                        className="mr-2"
                      />
                      Snacks
                    </label>
                  </div>
                </div>

                {/* Meal Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meal Description
                  </label>
                  <textarea
                    value={formData.mealDescription}
                    onChange={(e) => setFormData({ ...formData, mealDescription: e.target.value })}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Max Capacity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Capacity
                  </label>
                  <input
                    type="number"
                    value={formData.maxCapacity}
                    onChange={(e) => setFormData({ ...formData, maxCapacity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Teacher to Child Ratio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teacher:Child Ratio
                  </label>
                  <input
                    type="text"
                    value={formData.teacherToChildRatio}
                    onChange={(e) => setFormData({ ...formData, teacherToChildRatio: e.target.value })}
                    placeholder="e.g., 1:4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
              >
                {submitting ? 'Saving...' : packageData ? 'Update Package' : 'Create Package'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DaycarePackagesModal;